/**
 * EIA Open Data API — electricity pricing by state.
 * Free API key required (register at eia.gov/opendata).
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout } from './types';
import { resolveFipsFromRefdata } from '../refdata/fipsLookup';

export interface ElectricityData {
  retailRate: number;       // cents/kWh — state industrial realized average, not a tariff
  state: string;            // State abbreviation
  year: number;             // Data year from EIA
  utility?: string;         // Utility name
  basis?: 'state-industrial-average';
  sector?: 'IND';
  units?: 'cents per kilowatt-hour';
}

/** Supported EIA retail-sales query: state/sector context, never utility tariffs. */
export function buildEiaIndustrialStateUrl(state: string, includeSales = false): string {
  const params = new URLSearchParams({
    frequency: 'annual',
    'data[0]': 'price',
    ...(includeSales ? { 'data[1]': 'sales' } : {}),
    'facets[stateid][]': state,
    'facets[sectorid][]': 'IND',
    'sort[0][column]': 'period',
    'sort[0][direction]': 'desc',
    length: '1',
  });
  return `https://api.eia.gov/v2/electricity/retail-sales/data/?${params.toString()}`;
}

function finiteEiaNumber(value: unknown): number | null {
  if (typeof value !== 'number' && (typeof value !== 'string' || value.trim() === '')) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** Reject absent/mismatched scope or units instead of fabricating price or vintage. */
export function parseEiaIndustrialAnnualRecord(record: unknown, state: string, requireSales = false): {
  price: number;
  period: string;
  salesGWh: number | null;
} | null {
  if (!record || typeof record !== 'object') return null;
  const row = record as Record<string, unknown>;
  if (row.stateid !== state || row.sectorid !== 'IND' ||
      typeof row.period !== 'string' || !/^\d{4}$/.test(row.period) ||
      Number(row.period) < 2001 || Number(row.period) > new Date().getUTCFullYear() ||
      row['price-units'] !== 'cents per kilowatt-hour') return null;
  const price = finiteEiaNumber(row.price);
  if (price == null || price < 0 || price > 100) return null;
  let salesGWh: number | null = null;
  if (requireSales) {
    if (row['sales-units'] !== 'million kilowatt hours') return null;
    salesGWh = finiteEiaNumber(row.sales);
    if (salesGWh == null || salesGWh < 0) return null;
    // One million kWh is one GWh. The API differs from detailed EIA-861 MWh files.
  }
  return { price, period: row.period, salesGWh };
}

export const eiaElectricityProvider: DataProvider<ElectricityData> = {
  id: 'eia-electricity',
  name: 'EIA Electricity Rates',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false, // Key injected server-side by proxy

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<ElectricityData>> {
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`eia-electricity` is in CENTROID_QUERY_PROVIDER_IDS), else the
    // geocoded address. The rate is keyed off the state resolved from the
    // query point — a mailbox is essentially always in the same state as its
    // parcel, so this is a coordinate-consistency change; near a state line
    // it's the parcel that matters.
    // RD-1 (C5) — local-first state from the tiger-counties snapshot;
    // FCC Census Block API (free, no key) stays as the live fallback.
    let stateCode = (await resolveFipsFromRefdata(lat, lng))?.stateCode;
    if (!stateCode) {
      const fccRes = await fetchWithTimeout(
        `https://geo.fcc.gov/api/census/block/find?format=json&latitude=${lat}&longitude=${lng}`,
        15_000,
        geo?.signal,
      );
      if (!fccRes.ok) throw new Error('FCC geocoding failed');
      const fccJson = await fccRes.json();
      stateCode = fccJson.State?.code;
    }
    if (!stateCode) throw new Error('Could not determine state from coordinates');

    stateCode = stateCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(stateCode)) throw new Error('Could not determine a valid state from coordinates');
    const url = buildEiaIndustrialStateUrl(stateCode);
    const res = await fetchWithTimeout(url, 15_000, geo?.signal);
    if (!res.ok) throw new Error(`EIA: ${res.status}`);
    const json = await res.json();

    const record = json.response?.data?.[0];
    if (!record) throw new Error('EIA returned no electricity data');

    const context = parseEiaIndustrialAnnualRecord(record, stateCode);
    if (!context) throw new Error('EIA returned invalid industrial state context (scope, period, units or value)');
    const { price, period } = context;

    return {
      data: {
        retailRate: price,
        state: stateCode,
        year: Number(period),
        basis: 'state-industrial-average',
        sector: 'IND',
        units: 'cents per kilowatt-hour',
      },
      sources: [{
        field: 'electricity',
        source: `EIA — ${stateCode} state industrial average retail price: ${price.toFixed(2)} cents/kWh (${period}) — context only, not an applicable utility tariff`,
        url: 'https://www.eia.gov/electricity/data/state/',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
